import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { toEasternDateShort } from '../utils/time';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All leads' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
];
const STATUSES = ['new', 'contacted', 'converted', 'lost'];

function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('') || '?';
}

export default function LeadsPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() =>
    api.getLeads()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => toast('Failed to load leads', 'error'))
      .finally(() => setLoading(false)), [toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.updateLead(id, status);
      setItems(prev => prev.map(x => x.lead_id === id ? { ...x, status } : x));
      toast('Lead updated', 'success');
    } catch (e) {
      toast(e.message || 'Failed', 'error');
    }
  };

  const tc = useTableControls({
    items,
    searchFields: (l) => [l.contact_name, l.content_summary, l.source_type],
    statusOf: (l) => l.status,
    sorters: {
      timestamp: (l) => l.timestamp || '',
      contact_name: (l) => l.contact_name || '',
      status: (l) => l.status || '',
    },
    defaultSort: 'timestamp',
    defaultDir: 'desc',
    pageSize: 10,
  });

  return (
    <>
      <div className="page-header">
        <h1>Leads</h1>
      </div>

      {!loading && items.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{items.length}</span>
            <span className="summary-label">Total leads</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item attention">
            <span className="summary-value">{tc.counts.new || 0}</span>
            <span className="summary-label">New</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="summary-value">{tc.counts.contacted || 0}</span>
            <span className="summary-label">Contacted</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.converted || 0}</span>
            <span className="summary-label">Converted</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>All leads</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search leads…" />
            <button type="button" className="icon-btn" onClick={load} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs tabs={STATUS_TABS} active={tc.activeStatus} onChange={tc.setActiveStatus} counts={tc.counts} total={items.length} />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">⭐</div>
              <p>No leads yet</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No leads match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="timestamp" label="Date" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="contact_name" label="Name" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Source</th>
                  <th>What they said</th>
                  <SortableTh col="status" label="Status" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Update</th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(l => (
                  <tr key={l.lead_id}>
                    <td className="td-mono">{l.timestamp ? toEasternDateShort(l.timestamp) : '—'}</td>
                    <td>
                      <span className="name-cell">
                        <span className="name-avatar">{initials(l.contact_name)}</span>
                        {l.contact_name}
                      </span>
                    </td>
                    <td><span className={`badge badge-${l.source_type}`}>{l.source_type}</span></td>
                    <td className="td-truncate" style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                      {l.content_summary}
                    </td>
                    <td><span className={`badge badge-${l.status} badge-dot`}>{l.status}</span></td>
                    <td>
                      <select
                        className="form-input"
                        style={{ padding: '4px 8px', fontSize: '0.8rem', width: 120 }}
                        value={l.status}
                        onChange={e => updateStatus(l.lead_id, e.target.value)}
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
